import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  View,
  Flex,
  Text,
  Button,
  SearchField,
  Radio,
  RadioGroup,
} from "./Common/spectrumCompat";
import { displayToasts } from "../Helpers/displayAppToast";
import {
  getAccountId,
  persistPartnerLevel,
  formatCompanySelectAccountInfo,
  getDxpCompanyPageMessage,
  getDXPRegistrationLink,
  getUserData,
} from "../Helpers/utils";
import {
  setRegisteredCompanies,
  setSelectedRegisterCompanyType,
} from "../Redux/actions";
import DxpRegistrationTopHeader from "./DxpRegistrationTopHeader";
import WizardNavLayout from "./WizardNavLayout";
import selectCompanyConfig from "../ContentConfig/SelectCompanyStep";

const PROGRAM = "DXP";

/* eslint-disable @typescript-eslint/no-explicit-any */
interface CompanyItem {
  id?: string;
  recId?: string;
  name?: string;
  countryName?: string;
  country?: string;
  /** API value: typically `Technology` or `Solution` */
  primaryBusiness?: string;
  level?: string;
  status?: string;
}
/* eslint-enable @typescript-eslint/no-explicit-any */

const secondaryFooterPillLabelClass = "dxp-membership-secondary-footer-pill";

const primaryFooterPillLabelClass = "dxp-membership-primary-footer-pill";

function DxpCompanyMessageHtml({ html }: { html: string }) {
  // eslint-disable-next-line react/no-danger -- server-provided HTML copy
  return <span dangerouslySetInnerHTML={{ __html: html }} />;
}

type SelectYourCompanyProps = {
  previewMode?: boolean;
};

function SelectYourCompany({ previewMode = false }: SelectYourCompanyProps) {
  const dispatch = useDispatch();
  const registration = useSelector(
    (state: { registration?: Record<string, unknown> }) =>
      state?.registration || ({} as Record<string, unknown>),
  );
  const registeredCompanies: CompanyItem[] =
    (registration?.registeredCompanies as CompanyItem[]) || [];
  const email = (localStorage.getItem("email") as string) || "";
  const hasCompanies = registeredCompanies.length > 0;
  const selectedType =
    (registration?.selectedRegisterCompanyType as string) ||
    localStorage.getItem("selectedRegisterCompanyType");
  const isNewCompanyFlow =
    (selectedType === "standaloneCompany" ||
      selectedType === "subsidaryCompany") &&
    !localStorage.getItem("accountId");

  const [searchTerm, setSearchTerm] = useState("");
  const [filteredCompanies, setFilteredCompanies] =
    useState<CompanyItem[]>(registeredCompanies);
  const [selectedCompanyId, setSelectedCompanyId] = useState<string>("");
  const [userChoseAddCompany, setUserChoseAddCompany] = useState(false);
  const [selectedRegisterCompany, setSelectedRegisterCompany] = useState<
    "standaloneCompany" | "subsidaryCompany" | ""
  >("");
  const [narrowForCompanyType, setNarrowForCompanyType] = useState(
    () =>
      typeof window !== "undefined" &&
      window.matchMedia("(max-width: 640px)").matches,
  );

  const isPersistedCompanyType =
    selectedType === "standaloneCompany" || selectedType === "subsidaryCompany";
  const registerCompanyType =
    selectedRegisterCompany || (isPersistedCompanyType ? selectedType : "");
  const showAddCompanyView =
    !hasCompanies || userChoseAddCompany || isNewCompanyFlow;
  const showCompanyListView = hasCompanies && !showAddCompanyView;

  useEffect(() => {
    if (typeof window === "undefined") return undefined;
    const mq = window.matchMedia("(max-width: 640px)");
    const onChange = () => setNarrowForCompanyType(mq.matches);
    onChange();
    mq.addEventListener("change", onChange);
    return () => mq.removeEventListener("change", onChange);
  }, []);

  // Filter companies based on search
  useEffect(() => {
    if (!searchTerm.trim()) {
      setFilteredCompanies(registeredCompanies);
    } else {
      const term = searchTerm.toLowerCase();
      const filtered = registeredCompanies.filter((c) => {
        const meta = formatCompanySelectAccountInfo(c).toLowerCase();
        return (
          (c.name || "").toLowerCase().includes(term) || meta.includes(term)
        );
      });
      setFilteredCompanies(filtered);
    }
  }, [searchTerm, registeredCompanies]);

  const isUserLoggedIn = async (): Promise<boolean> => {
    try {
      if (
        typeof window !== "undefined" &&
        window.adobeIMS?.isSignedInUser?.()
      ) {
        const isValid = await window.adobeIMS?.validateToken?.();
        if (isValid) {
          const userData = getUserData();
          return !!(userData && userData.email);
        }
      }
      return false;
    } catch {
      return false;
    }
  };

  const handleBack = () => {
    if (previewMode) {
      displayToasts({
        type: "info",
        text: "Preview mode — back is disabled.",
        autoClose: true,
      });
      return;
    }
    dispatch(setSelectedRegisterCompanyType(null));
    localStorage.removeItem("selectedRegisterCompanyType");
    dispatch(setRegisteredCompanies(registeredCompanies, 0));
    const url = getDXPRegistrationLink();
    if (typeof window !== "undefined") {
      window.location.href = url;
    }
  };

  const handleCompanySelect = (companyId: string) => {
    setSelectedCompanyId(companyId);
  };

  const handleRegisterWithCompany = async () => {
    if (previewMode) {
      displayToasts({
        type: "info",
        text: "Preview mode — continue is disabled.",
        autoClose: true,
      });
      return;
    }
    if (!selectedCompanyId) return;
    const company = registeredCompanies.find(
      (c) => getAccountId(c) === selectedCompanyId,
    );
    if (!company) return;

    if (company.id || company.recId)
      localStorage.setItem("accountId", getAccountId(company));
    if (company.name) localStorage.setItem("companyName", company.name);
    persistPartnerLevel(company);
    if (company.status) localStorage.setItem("accountStatus", company.status);

    const nextStep = (await isUserLoggedIn()) ? 3 : 2;
    dispatch(setRegisteredCompanies(registeredCompanies, nextStep));
  };

  const handleRegisterNewCompany = async () => {
    if (previewMode) {
      displayToasts({
        type: "info",
        text: "Preview mode — continue is disabled.",
        autoClose: true,
      });
      return;
    }
    if (!registerCompanyType) return;
    localStorage.removeItem("accountId");
    localStorage.removeItem("companyName");
    localStorage.removeItem("emailMismatch");
    localStorage.removeItem("partnerRegistrationFormStep");
    localStorage.removeItem("RedirectPath");
    dispatch(setSelectedRegisterCompanyType(registerCompanyType));
    localStorage.setItem("selectedRegisterCompanyType", registerCompanyType);

    const nextStep = (await isUserLoggedIn()) ? 3 : 2;
    dispatch(setRegisteredCompanies(registeredCompanies, nextStep));
  };

  const handleToggleToAddCompany = () => {
    setUserChoseAddCompany(true);
    setSelectedCompanyId("");
  };

  let stepTotal = 3;
  if (selectedType === "subsidaryCompany") {
    stepTotal = 4;
  } else if (selectedType === "standaloneCompany") {
    stepTotal = 5;
  }
  const stepLabel = `STEP 1/${stepTotal}`;

  let previewNavCompanyType:
    | "standaloneCompany"
    | "subsidaryCompany"
    | null
    | undefined;
  if (!previewMode) {
    previewNavCompanyType = undefined;
  } else if (selectedType === "subsidaryCompany") {
    previewNavCompanyType = "subsidaryCompany";
  } else if (selectedType === "standaloneCompany") {
    previewNavCompanyType = "standaloneCompany";
  } else {
    previewNavCompanyType = null;
  }

  const companyTypeRadioClass = (isActive: boolean) =>
    [
      "dxp-s2-radio-card",
      "dxp-s2-radio-card--max-350",
      narrowForCompanyType
        ? "dxp-s2-radio-card--stacked-full"
        : "dxp-s2-radio-card--grow",
      isActive ? "dxp-s2-radio-card--active" : "dxp-s2-radio-card--inactive",
    ].join(" ");

  return (
    <div className="dxp-registration-page-shell registration-page-wrapper">
      <DxpRegistrationTopHeader program={PROGRAM} />
      <div className="dxp-registration-content-row registration-content-wrapper">
        <WizardNavLayout
          previewStepOverride={previewMode ? 1 : undefined}
          previewCompanyTypeOverride={previewNavCompanyType}
        />
        <View UNSAFE_className="dxp-wizard-main-column dxp-wizard-main-column--wide">
          <Flex direction="column" gap={0} alignItems="center">
            <View UNSAFE_className="dxp-wizard-step-panel dxp-mt-16">
              <Flex
                direction="row"
                justifyContent="space-between"
                alignItems="center"
                UNSAFE_className="dxp-wizard-step-heading-row dxp-wizard-flex-heading-row"
              >
                <Text UNSAFE_className="dxp-wizard-step-title-text">
                  {selectCompanyConfig.title}
                </Text>
                <Text UNSAFE_className="dxp-wizard-step-meta-label">
                  {stepLabel}
                </Text>
              </Flex>
              <div className="dxp-wizard-divider-purple" aria-hidden />

              {/* Company list view - when companies exist and not toggled to add */}
              {showCompanyListView ? (
                <>
                  <div
                    className="dxp-field-stack-gap-8"
                    style={{ marginBottom: "10px" }}
                  >
                    <div className="dxp-select-co-list-prompt">
                      {selectCompanyConfig.companyList.prompt}
                    </div>
                    <Text UNSAFE_className="dxp-select-co-html-prompt">
                      <DxpCompanyMessageHtml
                        html={getDxpCompanyPageMessage({
                          email,
                          noOfCompanies: registeredCompanies.length,
                        })}
                      />
                    </Text>
                  </div>
                  <View
                    UNSAFE_className="dxp-select-company-search-wrap dxp-w-full"
                    UNSAFE_style={{ marginBottom: "10px" }}
                  >
                    <SearchField
                      placeholder="Search for your company"
                      value={searchTerm}
                      onChange={setSearchTerm}
                      aria-label="Search companies"
                      UNSAFE_className="dxp-select-co-search-field"
                    />
                  </View>
                  <View UNSAFE_className="dxp-select-co-radio-scroll">
                    {filteredCompanies.length > 0 ? (
                      <RadioGroup
                        value={selectedCompanyId}
                        onChange={handleCompanySelect}
                        orientation="vertical"
                        aria-label="Select your company"
                        UNSAFE_className="dxp-w-full dxp-select-co-company-radio-group"
                      >
                        {filteredCompanies.map((company) => {
                          const id = getAccountId(company);
                          const accountMeta =
                            formatCompanySelectAccountInfo(company);
                          return (
                            <View
                              key={id}
                              UNSAFE_className="dxp-select-co-company-row"
                            >
                              <View UNSAFE_className="dxp-select-co-radio-cell">
                                <Radio value={id}>
                                  <Text UNSAFE_className="dxp-select-co-name-text">
                                    {company.name || `Company ${id}`}
                                  </Text>
                                </Radio>
                              </View>
                              {accountMeta ? (
                                <Text UNSAFE_className="dxp-select-co-country-text dxp-select-co-account-meta">
                                  {accountMeta}
                                </Text>
                              ) : null}
                            </View>
                          );
                        })}
                      </RadioGroup>
                    ) : null}
                    {filteredCompanies.length === 0 && searchTerm ? (
                      <Flex
                        justifyContent="center"
                        alignItems="center"
                        height="100%"
                      >
                        <Text UNSAFE_className="dxp-select-co-empty-search">
                          {`No companies found matching "${searchTerm}"`}
                        </Text>
                      </Flex>
                    ) : null}
                  </View>
                  <Flex
                    direction="row"
                    UNSAFE_className="dxp-select-co-footer-note"
                  >
                    <Text UNSAFE_className="dxp-select-co-note-text">
                      {selectCompanyConfig.companyList.continuePrefix}
                    </Text>

                    <Button
                      variant="secondary"
                      onPress={handleToggleToAddCompany}
                      UNSAFE_className="dxp-inline-text-link-btn"
                    >
                      {selectCompanyConfig.companyList.continueLink}
                    </Button>
                    <Text UNSAFE_className="dxp-select-co-note-text">
                      {selectCompanyConfig.companyList.continueSuffix}
                    </Text>
                  </Flex>
                  <Flex
                    direction="row"
                    justifyContent="space-between"
                    alignItems="center"
                    UNSAFE_className="dxp-wizard-footer-actions dxp-wizard-footer-actions--pair dxp-select-co-footer-actions"
                  >
                    <Button
                      variant="secondary"
                      UNSAFE_className={secondaryFooterPillLabelClass}
                      onPress={handleBack}
                    >
                      Back
                    </Button>
                    <Button
                      variant="accent"
                      isDisabled={!selectedCompanyId}
                      onPress={handleRegisterWithCompany}
                      UNSAFE_className={[
                        primaryFooterPillLabelClass,
                        !selectedCompanyId
                          ? "dxp-wizard-accent-next--disabled"
                          : "",
                      ]
                        .filter(Boolean)
                        .join(" ")}
                    >
                      Next
                    </Button>
                  </Flex>
                </>
              ) : null}

              {/* Add company view - standalone vs subsidiary */}
              {showAddCompanyView ? (
                <>
                  <div className="dxp-field-stack-gap-10">
                    <Text UNSAFE_className="dxp-select-co-intro-line">
                      Thanks for your interest in the partner program.
                    </Text>
                    <Text UNSAFE_className="dxp-select-co-intro-line dxp-body-text-mb-25">
                      Based on the email domain you entered, it looks like you
                      need to register your company. Is your company a
                      subsidiary of another company or a distinct company of its
                      own?
                    </Text>
                  </div>

                  <RadioGroup
                    value={registerCompanyType}
                    onChange={(v) =>
                      setSelectedRegisterCompany(
                        v as "standaloneCompany" | "subsidaryCompany",
                      )
                    }
                    orientation={
                      narrowForCompanyType ? "vertical" : "horizontal"
                    }
                    aria-label="Company type"
                    UNSAFE_className="dxp-company-type-radio-group dxp-w-full"
                  >
                    <Radio
                      value="standaloneCompany"
                      UNSAFE_className={companyTypeRadioClass(
                        registerCompanyType === "standaloneCompany",
                      )}
                    >
                      <Flex direction="column" gap={15}>
                        <Text UNSAFE_className="dxp-company-type-heading">
                          Register a new standalone company
                        </Text>
                        <Text UNSAFE_className="dxp-select-co-help-text">
                          Apply to the Digital Experience program by registering
                          a new company.
                        </Text>
                      </Flex>
                    </Radio>
                    <Radio
                      value="subsidaryCompany"
                      UNSAFE_className={companyTypeRadioClass(
                        registerCompanyType === "subsidaryCompany",
                      )}
                    >
                      <Flex direction="column" gap={15}>
                        <Text UNSAFE_className="dxp-company-type-heading">
                          Register as a subsidiary
                        </Text>
                        <Text UNSAFE_className="dxp-select-co-help-text">
                          Register a new company as a subsidiary of an approved
                          partner account.
                        </Text>
                      </Flex>
                    </Radio>
                  </RadioGroup>
                  <Text UNSAFE_className="dxp-select-co-fee-note">
                    {selectCompanyConfig.addCompany.feeNote}
                  </Text>
                  <Flex
                    direction="row"
                    justifyContent="space-between"
                    alignItems="center"
                    UNSAFE_className="dxp-wizard-footer-actions dxp-wizard-footer-actions--pair dxp-select-co-preview-footer"
                  >
                    <Button
                      variant="secondary"
                      UNSAFE_className={secondaryFooterPillLabelClass}
                      onPress={handleBack}
                    >
                      Back
                    </Button>
                    <Button
                      variant="accent"
                      isDisabled={!registerCompanyType}
                      onPress={handleRegisterNewCompany}
                      UNSAFE_className={[
                        primaryFooterPillLabelClass,
                        !registerCompanyType
                          ? "dxp-wizard-accent-next--disabled"
                          : "",
                      ]
                        .filter(Boolean)
                        .join(" ")}
                    >
                      Next
                    </Button>
                  </Flex>
                </>
              ) : null}
            </View>
          </Flex>
        </View>
      </div>
    </div>
  );
}

SelectYourCompany.defaultProps = {
  previewMode: false,
};

export default SelectYourCompany;
